interface GoogleCredentialResponse {
  credential: string;
}

interface AuthResponse {
  success: boolean;
  user?: {
    id: string;
    username: string;
    display_name: string;
    primary_email: string;
    image_url?: string;
  };
  tokens?: {
    access_token: string;
    refresh_token: string;
  };
  message?: string;
  code?: string;
}

class AuthService {
  private googleClientId = '';
  private appleClientId = '';

  async initializeAppleAuth(clientId: string) {
    console.log('🍎 Initializing Apple Sign-In with Client ID:', clientId);
    this.appleClientId = clientId;
    
    if (typeof window !== 'undefined' && window.AppleID) {
      console.log('✅ Apple Sign-In SDK loaded successfully');
      window.AppleID.auth.init({
        clientId: clientId,
        scope: 'name email',
        redirectURI: window.location.origin,
        state: 'origin:webplay',
        usePopup: true
      });
      console.log('✅ Apple Sign-In initialized with redirect:', window.location.origin);
    } else {
      console.warn('⚠️ Apple Sign-In SDK not loaded yet');
    }
  }

  async initializeGoogleAuth(clientId: string) {
    console.log('🔐 Initializing Google OAuth with Client ID:', clientId);
    this.googleClientId = clientId;
    
    if (typeof window !== 'undefined' && window.google) {
      console.log('✅ Google Sign-In SDK loaded successfully');
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: this.handleGoogleResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      console.log('✅ Google Sign-In initialized with callback');
    } else {
      console.warn('⚠️ Google Sign-In SDK not loaded yet');
    }
  }

  async googleSignIn(): Promise<AuthResponse> {
    console.log('🚀 Starting Google Sign-In process...');
    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.google) {
        console.log('✅ Google SDK available, setting up promise handlers');
        this.currentPromise = { resolve, reject };
        
        console.log('📱 Attempting to show Google Sign-In prompt...');
        window.google.accounts.id.prompt((notification: any) => {
          console.log('📝 Google Sign-In prompt notification:', notification);
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('⚠️ Prompt not displayed or skipped, showing fallback button');
            this.showGoogleSignInButton();
          } else {
            console.log('✅ Google Sign-In prompt displayed successfully');
          }
        });
      } else {
        console.error('❌ Google Sign-In not initialized');
        reject(new Error('Google Sign-In not initialized'));
      }
    });
  }

  private currentPromise: { resolve: (value: AuthResponse) => void; reject: (reason?: any) => void } | null = null;

  private async handleGoogleResponse(response: GoogleCredentialResponse) {
    console.log('🎉 Google OAuth callback received!');
    console.log('🔑 Credential token length:', response.credential.length);
    
    try {
      console.log('🌐 Sending credential to beemi-server for verification...');
      const authResponse = await this.authenticateWithBeemiServer(response.credential);
      
      console.log('📥 Beemi-server response:', authResponse);
      
      if (!authResponse.success) {
        console.error('❌ Authentication failed:', authResponse.message);
        if (this.currentPromise) {
          this.currentPromise.resolve(authResponse);
          this.currentPromise = null;
        }
        return;
      }
      
      if (authResponse.success && authResponse.tokens) {
        console.log('✅ Authentication successful!');
        console.log('👤 User:', authResponse.user);
        console.log('🎫 Tokens received - storing locally');
        
        this.storeTokens(authResponse.tokens);
        
        const storedTokens = this.getStoredTokens();
        if (!storedTokens || !storedTokens.refreshToken) {
          console.error('❌ Failed to store tokens properly - retrying...');
          await new Promise(resolve => setTimeout(resolve, 100));
          this.storeTokens(authResponse.tokens);
          
          const retriedTokens = this.getStoredTokens();
          if (!retriedTokens || !retriedTokens.refreshToken) {
            throw new Error('Failed to store authentication tokens');
          }
          console.log('✅ Tokens stored successfully on retry');
        } else {
          console.log('✅ Tokens verified in localStorage');
        }
      }
      
      if (this.currentPromise) {
        console.log('🔄 Resolving authentication promise');
        this.currentPromise.resolve(authResponse);
        this.currentPromise = null;
      }
    } catch (error) {
      console.error('💥 Google authentication error:', error);
      if (this.currentPromise) {
        console.log('🔄 Rejecting authentication promise with error');
        this.currentPromise.reject(error);
        this.currentPromise = null;
      }
    }
  }

  private showGoogleSignInButton() {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '50%';
    container.style.left = '50%';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.zIndex = '9999';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.borderRadius = '8px';
    container.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    
    document.body.appendChild(container);

    if (window.google) {
      window.google.accounts.id.renderButton(container, {
        theme: 'outline',
        size: 'large',
        width: '300'
      });
    }

    setTimeout(() => {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 10000);
  }

  private async authenticateWithBeemiServer(idToken: string): Promise<AuthResponse> {
    console.log('📦 Request payload: { idToken: [length=' + idToken.length + '], clientType: webplay }');
    
    try {
      const { apiService } = await import('./api');
      
      const response = await apiService.post<AuthResponse>('/auth/google', { 
        idToken,
        clientType: 'webplay'
      });
      console.log('✅ API Response Success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ API Error Response:', error.response?.data || error.message);
      const serverMessage = error.response?.data?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      } else {
        throw new Error(`Authentication failed: ${error.response?.status || 'Unknown'} - ${error.message}`);
      }
    }
  }

  async appleSignIn(): Promise<AuthResponse> {
    console.log('🚀 Starting Apple Sign-In process...');
    try {
      if (typeof window === 'undefined' || !window.AppleID) {
        throw new Error('Apple Sign-In not initialized');
      }

      console.log('📱 Showing Apple Sign-In popup...');
      const response = await window.AppleID.auth.signIn();
      
      console.log('🎉 Apple Sign-In response received');
      
      if (!response.authorization || !response.authorization.id_token) {
        throw new Error('No identity token received from Apple');
      }

      const authResponse = await this.authenticateWithAppleBackend(
        response.authorization.id_token,
        response.user
      );

      if (authResponse.success && authResponse.tokens) {
        console.log('✅ Apple authentication successful!');
        console.log('👤 User:', authResponse.user);
        
        this.storeTokens(authResponse.tokens);
        
        const storedTokens = this.getStoredTokens();
        if (!storedTokens || !storedTokens.refreshToken) {
          await new Promise(resolve => setTimeout(resolve, 100));
          this.storeTokens(authResponse.tokens);
        }
      }

      return authResponse;
    } catch (error: any) {
      console.error('💥 Apple sign-in error:', error);
      
      if (error.error === 'popup_closed_by_user') {
        throw new Error('Apple Sign-In was cancelled');
      }
      
      throw new Error(error?.message || 'Failed to sign in with Apple');
    }
  }

  private async authenticateWithAppleBackend(identityToken: string, user?: any): Promise<AuthResponse> {
    console.log('📦 Sending Apple token to beemi-server for verification');
    
    try {
      const { apiService } = await import('./api');
      
      const response = await apiService.post<AuthResponse>('/auth/apple', { 
        identityToken,
        user,
        clientType: 'webplay'
      });
      
      console.log('✅ Backend verification successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Backend verification failed:', error.response?.data || error.message);
      const serverMessage = error.response?.data?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      }
      throw new Error(`Apple authentication failed: ${error.response?.status || 'Unknown'}`);
    }
  }

  private storeTokens(tokens: { access_token: string; refresh_token: string }) {
    console.log('💾 Storing authentication tokens in localStorage');
    console.log('🔑 Access token length:', tokens.access_token.length);
    console.log('🔄 Refresh token length:', tokens.refresh_token.length);
    
    localStorage.setItem('beemi_webplay_access_token', tokens.access_token);
    localStorage.setItem('beemi_webplay_refresh_token', tokens.refresh_token);
    
    const storedAccess = localStorage.getItem('beemi_webplay_access_token');
    const storedRefresh = localStorage.getItem('beemi_webplay_refresh_token');
    
    console.log('🔍 Token storage verification:', {
      accessTokenStored: !!storedAccess,
      refreshTokenStored: !!storedRefresh,
      accessTokenMatches: storedAccess === tokens.access_token,
      refreshTokenMatches: storedRefresh === tokens.refresh_token,
    });
    
    if (!storedAccess || !storedRefresh) {
      console.error('❌ Failed to store tokens in localStorage');
      throw new Error('Failed to store authentication tokens');
    }
  }

  getStoredTokens() {
    const accessToken = localStorage.getItem('beemi_webplay_access_token');
    const refreshToken = localStorage.getItem('beemi_webplay_refresh_token');
    
    console.log('🔍 Token retrieval check:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
    });
    
    if (accessToken && refreshToken) {
      console.log('✅ Retrieved stored tokens from localStorage');
      return { accessToken, refreshToken };
    } else {
      console.log('❌ No stored tokens found in localStorage');
      return null;
    }
  }

  clearTokens() {
    console.log('🧹 Clearing authentication tokens from localStorage');
    localStorage.removeItem('beemi_webplay_access_token');
    localStorage.removeItem('beemi_webplay_refresh_token');
  }

  async getCurrentUser(): Promise<any> {
    console.log('👤 Getting current user from beemi-server...');
    const tokens = this.getStoredTokens();
    if (!tokens) {
      console.error('❌ No authentication tokens found');
      throw new Error('No authentication tokens found');
    }

    try {
      const { apiService } = await import('./api');
      
      const response = await apiService.get('/auth/me');
      console.log('✅ Current user retrieved:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to get current user:', error.response?.data || error.message);
      throw new Error(`Failed to get current user: ${error.response?.status || 'Unknown'}`);
    }
  }

  isAuthenticated(): boolean {
    return !!this.getStoredTokens();
  }

  logout() {
    this.clearTokens();
    if (typeof window !== 'undefined' && window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  }
}

export default new AuthService();

